import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star, X } from 'lucide-react';
import { cn } from '../lib/utils';

export const ImageCarousel = ({ 
  images = [], 
  mainImageIndex = 0,
  onSetMain,
  onRemove,
  editable = false,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className={cn("bg-gray-100 rounded-lg flex items-center justify-center aspect-video", className)}>
        <span className="text-gray-400">Sem imagens</span>
      </div>
    );
  }

  const goToPrevious = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className={cn("relative group", className)}>
      {/* Main Image */}
      <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-100">
        <img
          src={images[currentIndex]}
          alt={`Imagem ${currentIndex + 1}`}
          className="w-full h-full object-cover transition-opacity duration-300"
        />

        {/* Main badge */}
        {currentIndex === mainImageIndex && (
          <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs font-medium px-2 py-1 rounded flex items-center gap-1">
            <Star className="w-3 h-3" />
            Capa
          </div>
        )}

        {/* Edit controls */}
        {editable && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {currentIndex !== mainImageIndex && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSetMain?.(currentIndex);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-white p-1.5 rounded"
                title="Definir como capa"
              >
                <Star className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.(currentIndex);
              }}
              className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded"
              title="Remover imagem"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Image counter */}
        {images.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                "flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all",
                currentIndex === index 
                  ? "border-indigo-500 ring-2 ring-indigo-200" 
                  : "border-transparent hover:border-gray-300",
                index === mainImageIndex && "ring-2 ring-amber-400"
              )}
            >
              <img
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
